import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Editor } from "@tiptap/react"

import {
  Bold,
  ChevronUp,
  ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Redo,
  Undo,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { cn } from "@/lib/utils"

import { FormatToolbar } from "./format-toolbar"

type EmojiTarget = "words" | "sections" | "lists" | "remove"
type ReadingLevel =
  | "kindergarten"
  | "elementary"
  | "middle_school"
  | "high_school"
  | "college"
  | "graduate"
type LengthAdjustment = "much_shorter" | "shorter" | "keep" | "longer" | "much_longer"

type MobileFormatBarProps = {
  editor: Editor | null
  disabled?: boolean
  isAiProcessing?: boolean
  onPolish?: () => void
  onAddEmojis?: (target: EmojiTarget) => void
  onReadingLevelChange?: (level: ReadingLevel) => void
  onLengthChange?: (length: LengthAdjustment) => void
  onCustomAiEdit?: (instruction: string) => void
  onImageUpload?: () => void
}

export function MobileFormatBar({
  editor,
  disabled = false,
  isAiProcessing = false,
  onPolish,
  onAddEmojis,
  onReadingLevelChange,
  onLengthChange,
  onCustomAiEdit,
  onImageUpload,
}: MobileFormatBarProps) {
  const { t } = useTranslation()
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState("")
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false)
  const linkSelectionRef = useRef<{ from: number; to: number } | null>(null)
  const lastEditorSelectionRef = useRef<{ from: number; to: number } | null>(null)

  useEffect(() => {
    if (!editor) return

    const rememberSelection = () => {
      const { from, to } = editor.state.selection
      lastEditorSelectionRef.current = { from, to }
    }

    rememberSelection()
    editor.on("selectionUpdate", rememberSelection)
    return () => {
      editor.off("selectionUpdate", rememberSelection)
    }
  }, [editor])

  const normalizeLinkUrl = (value: string) => {
    if (value.startsWith("http://") || value.startsWith("https://")) {
      return value
    }
    return `https://${value}`
  }

  const getShortLinkLabel = (url: string) => {
    try {
      const hostname = new URL(url).hostname.replace(/^www\./, "")
      return hostname || url
    } catch {
      const trimmed = url.replace(/^https?:\/\//, "")
      const [host] = trimmed.split("/")
      return host || trimmed || url
    }
  }

  const handleSetLink = () => {
    if (!editor) return
    const trimmedUrl = linkUrl.trim()
    if (!trimmedUrl) return

    const url = normalizeLinkUrl(trimmedUrl)
    const fallbackSelection = editor.state.selection
    const targetSelection = linkSelectionRef.current ?? {
      from: fallbackSelection.from,
      to: fallbackSelection.to,
    }

    const chain = editor.chain().focus().setTextSelection(targetSelection)
    const hasSelectedText = targetSelection.from !== targetSelection.to
    const hasActiveLink = editor.isActive("link")

    if (!hasSelectedText && !hasActiveLink) {
      const label = getShortLinkLabel(url)
      chain
        .insertContent(label)
        .setTextSelection({
          from: targetSelection.from,
          to: targetSelection.from + label.length,
        })
        .setLink({ href: url })
        .run()
    } else {
      chain.extendMarkRange("link").setLink({ href: url }).run()
    }

    linkSelectionRef.current = null
    setLinkUrl("")
    setLinkPopoverOpen(false)
  }

  if (!editor) return null

  return (
    <>
      {/* Fixed bottom bar */}
      <div className="bg-background fixed inset-x-0 bottom-0 z-20 border-t pb-[env(safe-area-inset-bottom)]">
        <div
          className={cn(
            "flex h-12 items-center justify-between gap-1 px-2",
            disabled && "[&_button:disabled]:opacity-100"
          )}
        >
          {/* Undo/Redo */}
          <div className="flex items-center">
            <Button
              className="size-10"
              disabled={disabled || !editor.can().undo()}
              size="icon"
              variant="ghost"
              onClick={() => editor.chain().focus().undo().run()}
            >
              <Undo className="size-5" />
            </Button>
            <Button
              className="size-10"
              disabled={disabled || !editor.can().redo()}
              size="icon"
              variant="ghost"
              onClick={() => editor.chain().focus().redo().run()}
            >
              <Redo className="size-5" />
            </Button>
          </div>

          {/* Formatting actions */}
          <div className="flex items-center gap-0.5">
            <Button
              disabled={disabled}
              size="icon"
              variant="ghost"
              className={cn(
                "size-10",
                editor.isActive("bold") && "border-foreground bg-accent border"
              )}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <Bold className="size-5" />
            </Button>
            <Button
              disabled={disabled}
              size="icon"
              variant="ghost"
              className={cn(
                "size-10",
                editor.isActive("italic") && "border-foreground bg-accent border"
              )}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <Italic className="size-5" />
            </Button>
            <Button
              disabled={disabled}
              size="icon"
              variant="ghost"
              className={cn(
                "size-10",
                editor.isActive("bulletList") && "border-foreground bg-accent border"
              )}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <List className="size-5" />
            </Button>
            <Button
              disabled={disabled}
              size="icon"
              variant="ghost"
              className={cn(
                "size-10",
                editor.isActive("orderedList") && "border-foreground bg-accent border"
              )}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <ListOrdered className="size-5" />
            </Button>

            {/* Link popover */}
            <Popover
              open={linkPopoverOpen}
              onOpenChange={(open) => {
                if (open) {
                  linkSelectionRef.current = lastEditorSelectionRef.current
                } else {
                  linkSelectionRef.current = null
                }
                setLinkPopoverOpen(open)
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  disabled={disabled}
                  size="icon"
                  variant="ghost"
                  className={cn(
                    "size-10",
                    editor.isActive("link") && "border-foreground bg-accent border"
                  )}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    linkSelectionRef.current = lastEditorSelectionRef.current
                  }}
                >
                  <LinkIcon className="size-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72" side="top">
                <div className="flex flex-col gap-2">
                  <Input
                    placeholder={t("canvas.link_url_placeholder", "Enter URL...")}
                    value={linkUrl}
                    onChange={(event) => setLinkUrl(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault()
                        handleSetLink()
                      }
                    }}
                  />
                  <div className="flex gap-2">
                    {editor.isActive("link") && (
                      <Button
                        className="flex-1"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const fallbackSelection = editor.state.selection
                          const targetSelection = linkSelectionRef.current ?? {
                            from: fallbackSelection.from,
                            to: fallbackSelection.to,
                          }
                          editor.chain().focus().setTextSelection(targetSelection).unsetLink().run()
                          linkSelectionRef.current = null
                          setLinkPopoverOpen(false)
                        }}
                      >
                        {t("canvas.remove_link", "Remove")}
                      </Button>
                    )}
                    <Button
                      className="flex-1"
                      disabled={!linkUrl.trim()}
                      size="sm"
                      onClick={handleSetLink}
                    >
                      {t("canvas.set_link", "Set Link")}
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Image upload */}
            {onImageUpload && (
              <Button
                className="size-10"
                disabled={disabled}
                size="icon"
                variant="ghost"
                onClick={onImageUpload}
              >
                <ImageIcon className="size-5" />
              </Button>
            )}
          </div>

          {/* More button */}
          <Button
            className="h-10 gap-1 px-3"
            disabled={disabled}
            size="sm"
            variant="outline"
            onClick={() => setIsSheetOpen(true)}
          >
            <span className="text-sm">{t("canvas.more", { defaultValue: "More" })}</span>
            <ChevronUp className="size-4" />
          </Button>
        </div>
      </div>

      {/* Bottom sheet with full toolbar */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="h-[70vh] px-0 pb-[env(safe-area-inset-bottom)]" side="bottom">
          <SheetHeader className="px-4 pb-2">
            <SheetTitle>{t("canvas.formatting", { defaultValue: "Formatting" })}</SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-full px-4">
            <div className="pb-6">
              <FormatToolbar
                disabled={disabled}
                editor={editor}
                isAiProcessing={isAiProcessing}
                onAddEmojis={onAddEmojis}
                onCustomAiEdit={onCustomAiEdit}
                onImageUpload={onImageUpload}
                onLengthChange={onLengthChange}
                onPolish={onPolish}
                onReadingLevelChange={onReadingLevelChange}
              />
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </>
  )
}
